/**
 * Shared types and utilities for Zustand stores
 */

export interface CacheConfig {
  ttl: number; // milliseconds - time to live for cache
  staleTime: number; // milliseconds - when to background refetch
}

export interface CacheableState<T> {
  data: T[];
  byId: Record<string, T>;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  isFetching: boolean; // for background refetches
}

export const CACHE_TTL = {
  SHORT: 2 * 60 * 1000,    // 2 min - frequently changing data
  MEDIUM: 5 * 60 * 1000,   // 5 min - moderately stable data
  LONG: 15 * 60 * 1000,    // 15 min - rarely changes
} as const;

/**
 * Helper function to index an array of items by their ID
 */
export function indexById<T extends { id: string }>(items: T[]): Record<string, T> {
  return items.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {} as Record<string, T>);
}

/**
 * Helper function to check if cache is still valid
 */
export function isCacheValid(lastFetched: number | null, ttl: number): boolean {
  if (!lastFetched) return false;
  return Date.now() - lastFetched < ttl;
}

